import { supabase } from '@/lib/supabase';
import { getEdgeFunctionErrorMessage } from '@/lib/serviceError';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface SyncResult {
  message: string;
  stationsAdded: number;
  stationsUpdated: number;
  totalStations?: number;
}

export interface StreamCheckResult {
  message: string;
  checked: number;
  working: number;
  broken: number;
}

async function callEdgeFunction<T>(name: string, body?: Record<string, unknown>): Promise<T> {
  const url = `${SUPABASE_URL}/functions/v1/${name}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
  };

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : null,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(getEdgeFunctionErrorMessage(name, response.status, errorText));
  }

  const data = await response.json();
  if (data.error) {
    throw new Error(data.error);
  }
  return data as T;
}

export const syncService = {
  triggerSync(): Promise<SyncResult> {
    return callEdgeFunction<SyncResult>('sync-stations');
  },

  checkStream(stationId: string): Promise<StreamCheckResult> {
    return callEdgeFunction<StreamCheckResult>('check-stream', { station_id: stationId });
  },

  checkAllStreams(): Promise<StreamCheckResult> {
    return callEdgeFunction<StreamCheckResult>('check-stream');
  },

  async getSyncStatus(): Promise<{ status: string; startedAt: string; finishedAt: string | null; stationsAdded: number; stationsUpdated: number } | null> {
    const { data, error } = await supabase
      .from('sync_log')
      .select('status, started_at, finished_at, stations_added, stations_updated')
      .order('started_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return {
      status: data.status,
      startedAt: data.started_at,
      finishedAt: data.finished_at,
      stationsAdded: data.stations_added,
      stationsUpdated: data.stations_updated,
    };
  },
};
